import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './ScanHistory.css';
import { Button } from '@/components/ui/button';

interface ScanHistoryProps {
  onRescan: (title: string, year?: string, type?: string, priority?: string) => void;
  onViewResults: (batchId: string) => void;
}

export default function ScanHistory({ onRescan, onViewResults }: ScanHistoryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const location = useLocation();
  const navigate = useNavigate();

  const recentScans = useQuery(api.scans.getRecentScans, { limit: 100 });
  const monitored = useQuery(api.monitoring.getMonitored);
  const addToMonitoring = useMutation(api.monitoring.addToMonitoring);
  const cancelScan = useMutation(api.scans.cancelScan);

  // Build batchIds for scheduled runs query
  const batchIdsArg = recentScans ? Array.from(new Set(recentScans.map(s => s.batchId))) : [] as string[];
  const scheduledRuns = useQuery(api.scheduler.getScheduledRunsForBatches, { batchIds: batchIdsArg });

  // Initialize from global search (query param) or legacy localStorage handoff
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const q = params.get('q');
    if (q && q !== searchTerm) {
      setSearchTerm(q);
      return;
    }
    // Legacy support
    const preset = localStorage.getItem('historySearchTerm');
    if (preset) {
      setSearchTerm(preset);
      localStorage.removeItem('historySearchTerm');
    }
  }, [location.search]);

  if (!recentScans || !scheduledRuns) {
    return <div className="loading">Loading scan history...</div>;
  }

  // Group scans by batch
  const batchMap = new Map<string, any[]>();
  for (const scan of recentScans) {
    if (!batchMap.has(scan.batchId)) {
      batchMap.set(scan.batchId, []);
    }
    batchMap.get(scan.batchId)!.push(scan);
  }

  // Convert to array of batches
  const batches = Array.from(batchMap.entries()).map(([batchId, scans]) => {
    const now = Date.now();
    const maxScanDuration = 60 * 60 * 1000; // 1 hour timeout

    // Check if any scan is stuck (scanning for > 1 hour)
    const hasStuckScan = scans.some((s: any) => {
      const startedOrCreated = (s.startedAt ?? s.createdAt) as number;
      return s.status === 'scanning' && startedOrCreated && (now - startedOrCreated) > maxScanDuration;
    });

    // Determine batch status
    let status: string;
    if (scans.every(s => s.status === 'complete')) {
      status = 'complete';
    } else if (scans.some(s => s.status === 'error')) {
      status = 'error';
    } else if (hasStuckScan) {
      status = 'incomplete';
    } else if (scans.some(s => s.status === 'scanning')) {
      status = 'scanning';
    } else {
      status = 'queued';
    }

    return {
      batchId,
      scans,
      startedAt: Math.min(...scans.map((s: any) => (s.startedAt ?? s.createdAt))),
      completedAt: Math.max(...scans.map((s: any) => (s.completedAt ?? s.startedAt ?? s.createdAt))),
      totalViolations: scans.reduce((sum, s) => sum + s.violationCount, 0),
      status,
    };
  });

  // Filter batches
  const filteredBatches = batches.filter(batch => {
    if (filterStatus !== 'all' && batch.status !== filterStatus) {
      return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return batch.scans.some(s => s.contentTitle.toLowerCase().includes(term));
    }
    return true;
  });

  // Sort by most recent
  filteredBatches.sort((a, b) => b.startedAt - a.startedAt);

  // Helper to check if a title is already monitored
  const isMonitored = (title: string) => {
    return monitored?.some(m => m.contentTitle === title) || false;
  };

  // Handler for adding to monitoring
  const handleAddToMonitoring = async (scan: any) => {
    await addToMonitoring({
      contentTitle: scan.contentTitle,
      contentYear: scan.contentYear,
      contentType: scan.contentType,
      priority: scan.priority,
      scanFrequency: 'weekly', // Default to weekly
    });
  };

  // Handler for cancelling a scan
  const handleCancelScan = async (scanId: string) => {
    if (!confirm('Cancel this scan? It will be marked as failed.')) {
      return;
    }
    try {
      await cancelScan({ scanId: scanId as any });
    } catch (error: any) {
      alert(`Failed to cancel scan: ${error.message}`);
    }
  };

  return (
    <div className="scan-history">
      <div className="history-meta">
        <p className="history-subtitle">
          {batches.length} scan sessions • {recentScans.length} total titles scanned
        </p>
      </div>

      <div className="history-filters">
        <input
          type="text"
          placeholder="Search by title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="complete">Completed</option>
          <option value="scanning">In Progress</option>
          <option value="incomplete">Incomplete/Timeout</option>
          <option value="error">Failed</option>
        </select>
      </div>

      <div className="history-list">
        {filteredBatches.length === 0 ? (
          <div className="empty-state">
            <p>No scan history found</p>
            {searchTerm && <p className="empty-hint">Try adjusting your search</p>}
          </div>
        ) : (
          filteredBatches.map(batch => {
            const scansToShow = searchTerm
              ? batch.scans.filter((s: any) => s.contentTitle.toLowerCase().includes(searchTerm.toLowerCase()))
              : batch.scans;
            return (
            <div key={batch.batchId} className="history-batch card">
              <div className="batch-header">
                <div className="batch-info">
                  <span className="batch-date">
                    {new Date(batch.startedAt).toLocaleDateString()} at{' '}
                    {new Date(batch.startedAt).toLocaleTimeString()}
                  </span>
                  <span className={`batch-status status-${batch.status}`}>
                    {batch.status}
                  </span>
                </div>
                <div className="batch-stats">
                  <span className="stat">
                    {scansToShow.length} title{scansToShow.length !== 1 ? 's' : ''}
                  </span>
                  <span className="stat violations">
                    {batch.totalViolations} violation{batch.totalViolations !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="batch-scans">
                {scansToShow.map(scan => (
                  <div key={scan._id} className="history-scan">
                    <div className="scan-title-section">
                      <h4 className="scan-title">{scan.contentTitle}</h4>
                      <div className="scan-metadata">
                        {scan.contentYear && <span className="meta-tag">{scan.contentYear}</span>}
                        {scan.contentType && <span className="meta-tag">{scan.contentType}</span>}
                        {scan.priority && (
                          <span className={`meta-tag priority-${scan.priority}`}>
                            {scan.priority} priority
                          </span>
                        )}
                        {/* Scheduled run id note, if exists */}
                        {(() => {
                          const sr = scheduledRuns.find((r: any) => r.batchId === batch.batchId && r.contentTitle === scan.contentTitle);
                          return sr ? (<span className="meta-tag scheduled-note">Scheduled ({sr.runId.slice(0,8)}…)</span>) : null;
                        })()}
                      </div>
                    </div>

                    <div className="scan-results">
                      <span className={`violations-badge ${scan.violationCount > 0 ? 'has-violations' : ''}`}>
                        {scan.violationCount} violations
                      </span>

                      {(scan.status === 'scanning' || scan.status === 'queued') && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancelScan(scan._id)}
                          title="Cancel this scan"
                        >
                          ✕ Cancel
                        </Button>
                      )}

                      <Button
                        variant={isMonitored(scan.contentTitle) ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() => handleAddToMonitoring(scan)}
                        title={isMonitored(scan.contentTitle) ? 'Already monitored' : 'Add to monitoring'}
                        disabled={isMonitored(scan.contentTitle)}
                      >
                        {isMonitored(scan.contentTitle) ? '✓ Monitored' : '📊 Add to Monitoring'}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRescan(
                          scan.contentTitle,
                          scan.contentYear,
                          scan.contentType,
                          scan.priority
                        )}
                        title="Rescan this title"
                      >
                        🔄 Rescan
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="batch-actions">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Extract scan data and format as ContentInput[]
                    const rescanContent = batch.scans.map(scan => ({
                      title: scan.contentTitle,
                      arabicTitle: scan.arabicTitle,
                      year: scan.contentYear,
                      type: scan.contentType as 'series' | 'film' | 'original' | undefined,
                      priority: scan.priority as 'high' | 'medium' | 'low' | undefined,
                    }));

                    // Navigate to new scan page with pre-populated data
                    navigate('/new-scan', { state: { rescanContent } });
                  }}
                >
                  🔄 Batch Rescan
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    try { localStorage.setItem('dashboard:autoExpand', '1'); } catch {}
                    onViewResults(batch.batchId);
                  }}
                >
                  View Results →
                </Button>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}
